import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classname from 'classnames';
import {
  Buttons,
  IF,
} from 'lidya-common-components';
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next';
import { useGlobalData } from '../../../providers/globalDataProvider/globalDataProvider.useGlobalData';
import { GetExternalLinkButton } from '../../common/externalLinks/external.link.button';
import { useDataLayer } from '../../../hooks/useDataLayer';
import { useModalData } from '../../common/providers/modalProvider/modalDataProvider.useModalData';
import { SignUpButton } from '../signUp/sign.up.button';
import { NavContactElement } from '../navContactElement';
import { ModalDataProvider } from '../../common/providers/modalProvider/modalDataProvider.provider';
import {
  SignUpModalComponent,
} from '../signUp/sign.up.modal.component';
import { COUNTRY_LANGUAGE_LIST } from '../../common/webSiteLanguages';
import { ContextLink } from '../../common/contextLink';
import {
  getLinkByComponentName,
} from '../../../hooks/useNavigationLinks';
import { POSSIBLE_FORMS } from '../signUp/sign.up.memo.form';

const POLICIES = ['terms_conditions', 'privacy_policy', 'nbcb'];

function GetLoanButtonMenu({ id, className }) {
  return <GetExternalLinkButton id={id} className={className} />;
}

GetLoanButtonMenu.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
};

GetLoanButtonMenu.defaultProps = {
  className: undefined,
};

function CreateAccountAndLoginComponent({ className, id }) {
  const { toggleModal } = useModalData();
  const { t } = useTranslation();
  const registerEvent = useDataLayer();

  return (
    <Buttons.ButtonWithMore
      className={classname(
        'a-navigation__starterButton',
        '-sign-up-button',
        className,
      )}
      id={`${id}-Group`}
      onClick={toggleModal}
      label={t('web.navigation.create.account')}
      type={Buttons.BUTTON_TYPES.SECONDARY}
      sideButtonClassName={classname(
        'a-button a-navigation__starterButton',
        '-sign-up-button',
        className,
      )}
      options={[
        <GetExternalLinkButton
          className={className}
          label="web.navigation.sign.in"
          href="https://app.lidya.info"
          id="GetLoginButtonMenu"
          key="GetLoginButtonMenu"
          onClick={() => registerEvent({ event: 'login-button-click' })}
        />,
      ]}
    />
  );
}

CreateAccountAndLoginComponent.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
};

CreateAccountAndLoginComponent.defaultProps = {
  className: undefined,
};

function SignUpButtonComponent({ id, className }) {
  return (
    <SignUpButton
      id={`SignUpButton_${id}`}
      label="web.navigation.sign.up"
      className={classname('a-navigation__starterButton', className)}
      type={Buttons.BUTTON_TYPES.PRIMARY}
    />
  );
}

SignUpButtonComponent.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
};

SignUpButtonComponent.defaultProps = {
  className: undefined,
};

function SignInButtonWithWebApp() {
  const registerEvent = useDataLayer();

  return (
    <>
      <GetExternalLinkButton
        className={classname('button-desktop', '-sign-up-button', 'a-navigation__starterButton')}
        label="web.navigation.sign.up"
        href="https://webapp.lidya.info/signup"
        id="CreateHor"
        type={Buttons.BUTTON_TYPES.SECONDARY}
        onClick={() => registerEvent({ event: 'sign-up-button-click' })}
      />

      <GetExternalLinkButton
        className="button-desktop"
        label="web.navigation.sign.in"
        href="https://webapp.lidya.info"
        id="LoginHor"
        type={Buttons.BUTTON_TYPES.PRIMARY}
        onClick={() => registerEvent({ event: 'login-button-click' })}
      />
    </>
  );
}

function SignInButton({ isCreateAccount, id, className }) {
  return (
    <ModalDataProvider>
      {isCreateAccount ? (
        <CreateAccountAndLoginComponent className={className} id={id} />
      ) : (
        <SignUpButtonComponent className={className} id={id} />
      )}
      <SignUpModalComponent
        signupType={POSSIBLE_FORMS.SIGN_UP}
        isCreateAccount={isCreateAccount}
      />
    </ModalDataProvider>
  );
}

SignInButton.propTypes = {
  id: PropTypes.string.isRequired,
  isCreateAccount: PropTypes.bool,
  className: PropTypes.string,
};

SignInButton.defaultProps = {
  isCreateAccount: false,
  className: undefined,
};

function NavigationTerms({ componentName }) {
  const { country, language } = useGlobalData();
  const link = getLinkByComponentName({ componentName, country }) || false;
  const slug = link[`slug_${language}`];
  const { new_tab: newTab } = link;
  return (
    <IF condition={!!link}>
      <div className="m-navigationTerms">
        <ContextLink
          id={`NavigationTerms${slug}`}
          to={slug}
          className="a-navigationTerms__link"
          target={newTab ? '_blank' : undefined}
        >
          {link.label}
        </ContextLink>
      </div>
    </IF>
  );
}

NavigationTerms.propTypes = {
  componentName: PropTypes.string,
};

NavigationTerms.defaultProps = {
  componentName: 'terms_conditions',
};

const NavigationCopyright = function () {
  const year = new Date().getFullYear();
  return (
    <p>
      <Trans values={{ year }}>web.footer.copyright</Trans>
    </p>
  );
};

export const useNavButtons = ({ noActionsAvailable, CustomNavAction }) => {
  const { country } = useGlobalData();
  const { hasOperations, hasWebApp, canGetLoan } = COUNTRY_LANGUAGE_LIST[country];

  const CustomHorizontalButtons = useMemo(
    () => (
      <>
        <NavContactElement
          className={classname('contacts-desktop', { '-not-alone': (hasOperations || canGetLoan) && !noActionsAvailable })}
        />
        <IF condition={hasOperations && canGetLoan && !noActionsAvailable}>
          <GetLoanButtonMenu
            key={`sigin_button_h_${country}`}
            id="SignInHor"
            className="button-desktop"
          />
        </IF>

        <IF condition={hasOperations && hasWebApp && !noActionsAvailable}>
          <SignInButtonWithWebApp />
        </IF>

        <IF condition={hasOperations && !noActionsAvailable && !hasWebApp}>
          <SignInButton
            key={`login_button_h_${country}`}
            id="LoginHor"
            className="button-desktop"
          />
        </IF>
        {CustomNavAction && <CustomNavAction className="a-landingPageNavButton" />}
      </>
    ),
    [CustomNavAction, canGetLoan, country, hasOperations, hasWebApp, noActionsAvailable],
  );

  const CustomVerticalButtons = useCallback(
    ({ isMenuOpen, toggleMenu }) => (
      <>
        <NavContactElement className="contacts-mobile" />
        <IF condition={hasOperations && canGetLoan && !noActionsAvailable}>
          <GetLoanButtonMenu key={`sigin_button_v_${country}`} id="SignInVer" />
        </IF>
        <IF condition={hasOperations && !noActionsAvailable && !hasWebApp}>
          <SignInButton key={`login_button_v_${country}`} id="LoginVer" />
        </IF>
        <IF condition={hasOperations && hasWebApp && !noActionsAvailable}>
          <SignInButtonWithWebApp />
        </IF>
        {CustomNavAction && <CustomNavAction isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />}
      </>
    ),
    [CustomNavAction, canGetLoan, country, hasOperations, hasWebApp, noActionsAvailable],
  );

  const VerticalMenuComplement = useMemo(() => (
    <>
      { POLICIES.map((policy) => (
        <NavigationTerms
          key={`nav_${policy}_${country}`}
          componentName={policy}
        />
      ))}
      <NavigationCopyright key={`nav_copyright_${country}`} />
    </>

  ), [country]);

  return { CustomHorizontalButtons, CustomVerticalButtons, VerticalMenuComplement };
};

useNavButtons.propTypes = {};

export default useNavButtons;
